import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useVacationModuleAccess } from '@/features/Vacations/hooks/useVacationModuleAccess';
import { MyRequestDrawer, MyRequestsSection, useMyDpRequestsQuery } from '@/features/DpRequests';
import { DpRequestCard } from '../../components/DpRequestCard';
import { DP_PORTAL_BASE_PATH } from '../../dpPortal.definitions';
import { useUpcomingRequestsVisibility } from '../../hooks/useUpcomingRequestsVisibility';
import {
    COMING_SOON_BADGE,
    DP_REQUEST_CARDS,
    UNAVAILABLE_BADGE,
    UPCOMING_HIDE_LABEL,
    UPCOMING_SHOW_LABEL,
} from './dpRequestsPage.definitions';
import type { DpRequestCardDefinition } from './dpRequestsPage.types';

const AVAILABLE_CARDS = DP_REQUEST_CARDS.filter(card => card.tabId || card.path);
const UPCOMING_CARDS_COUNT = DP_REQUEST_CARDS.length - AVAILABLE_CARDS.length;
const REQUEST_ID_PARAM = 'requestId';

function cardHref(card: DpRequestCardDefinition): string | undefined {
    if (card.path) return `${DP_PORTAL_BASE_PATH}/${card.path}`;
    if (card.tabId) return `${DP_PORTAL_BASE_PATH}/${card.tabId}`;
    return undefined;
}

function resolveCard(card: DpRequestCardDefinition, canSeeModule: boolean, isLoading: boolean) {
    const needsModule = Boolean(card.needsVacationModule);
    const href = cardHref(card);

    if (href && (!needsModule || (!isLoading && canSeeModule))) {
        return { to: href, badge: undefined };
    }

    if (needsModule && isLoading) {
        return { to: undefined, badge: undefined };
    }

    return { to: undefined, badge: href ? UNAVAILABLE_BADGE : COMING_SOON_BADGE };
}

export function DpRequestsPage() {
    const { user } = useAuth();
    const email = user?.email ?? null;
    const { canSeeModule, isLoading } = useVacationModuleAccess();
    const { showUpcoming, toggle } = useUpcomingRequestsVisibility();
    const mineQuery = useMyDpRequestsQuery(email);
    const [searchParams, setSearchParams] = useSearchParams();
    const openRequestId = searchParams.get(REQUEST_ID_PARAM);
    const cards = showUpcoming ? DP_REQUEST_CARDS : AVAILABLE_CARDS;

    return (
        <div className="space-y-8">
            <div className="space-y-4">
                <div className="flex justify-end">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggle}
                        aria-pressed={showUpcoming}
                        leftIcon={showUpcoming ? <EyeSlashIcon className="h-4 w-4" aria-hidden="true" /> : <EyeIcon className="h-4 w-4" aria-hidden="true" />}
                    >
                        {showUpcoming ? UPCOMING_HIDE_LABEL : UPCOMING_SHOW_LABEL(UPCOMING_CARDS_COUNT)}
                    </Button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {cards.map(card => {
                        const { to, badge } = resolveCard(card, canSeeModule, isLoading);
                        return (
                            <DpRequestCard
                                key={card.id}
                                title={card.title}
                                description={card.description}
                                note={card.note}
                                icon={card.icon}
                                to={to}
                                badge={badge}
                            />
                        );
                    })}
                </div>
            </div>

            {mineQuery.isPending && <div className="h-48 animate-pulse rounded-2xl bg-raro-gray-100" />}
            {mineQuery.isError && (
                <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                    Não foi possível carregar seus pedidos.
                </p>
            )}
            {mineQuery.data && (
                <MyRequestsSection
                    requests={mineQuery.data}
                    onOpen={requestId => setSearchParams({ [REQUEST_ID_PARAM]: requestId })}
                />
            )}

            <MyRequestDrawer
                requestId={openRequestId}
                viewerEmail={email ?? ''}
                onClose={() => setSearchParams({})}
            />
        </div>
    );
}

export default DpRequestsPage;
